import React, { useEffect, useState } from 'react';
import { Chip, CircularProgress, Tooltip } from '@mui/material';
import { ResourceUsageSummary } from '@/types/resource-usage';

interface Props {
  id: number;
  fetchUsage: (id: number) => Promise<ResourceUsageSummary>;
}

/**
 * Єдиний усереднений показник завантаженості користувача (% від threshold, серед. по категоріях).
 * Значення вже розраховане й збережене на бекенді (nest_user.resource_usage_percent) —
 * компонент лише відображає те, що повернув переданий `fetchUsage`.
 * Сама довантажує дані незалежно (та сама архітектура, що ResourceUsageCell — перевикористовується
 * і в адмінці, і на сторінці клієнта для експерта).
 */
export const ResourceUsageAverageCell: React.FC<Props> = ({ id, fetchUsage }) => {
  const [summary, setSummary] = useState<ResourceUsageSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setSummary(null);
    setLoading(true);
    fetchUsage(id)
      .then(setSummary)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id, fetchUsage]);

  if (loading) {
    return <CircularProgress size={14} />;
  }

  if (!summary) {
    return <>—</>;
  }

  const percent = summary.averagePercent;
  const color = percent >= 100 ? 'error' : percent >= 70 ? 'warning' : 'success';

  return (
    <Tooltip title="Середнє по всіх категоріях ресурсів, % від порогового значення">
      <Chip label={`${percent}%`} color={color} size="small" variant="outlined" />
    </Tooltip>
  );
};
